/* eslint-disable consistent-return */
import { CheckCircleFilled } from "@ant-design/icons";
import { TriggerCreate } from "@dots-projects/types";
import { Alert, Button, Col, Layout, Row, Spin } from "antd";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import supabase, { getSupaClient, getWorkspace } from "../../lib/supabase";
import { useDotsAdminsChannelUrl } from "../DiscordBotAdded/DiscordBotAdded";
import "../DiscordBotAdded/DiscordBotAdded.css";
import { bookmarkMessages } from "../DiscordTriggers/templates/bookmarkMessages";
import { collectEmail } from "../DiscordTriggers/templates/collectEmail";
import { collectFeedback } from "../DiscordTriggers/templates/collectFeedback";
import { emailVerification } from "../DiscordTriggers/templates/emailVerification";
import { eventRSVP } from "../DiscordTriggers/templates/eventRSVP";
import { privateOnboardingTemplate } from "../DiscordTriggers/templates/privateOnboarding";
import { publicOnboardingTemplate } from "../DiscordTriggers/templates/publicOnboarding";
import { sendSurveyTemplate } from "../DiscordTriggers/templates/sendSurvey";
import { suportTicketTemplate } from "../DiscordTriggers/templates/supportTicket";
import { setOnboardingStatus } from "../Onboarding/Onboarding";
import { persistTrigger } from "../TriggerEditor/useTrigger";
import "./DiscordAllSet.css";

const { Content } = Layout;

type BotOption = {
  id: number;
  name: string;
  icon: string;
  active: boolean;
  color: string;
  key: string;
};

const botOptions = [
  {
    id: 1,
    name: "Support ticket",
    icon: "❗",
    active: false,
    color: "#e6fcf5",
    key: "support-ticket",
  },
  {
    id: 2,
    name: "User survey",
    icon: "❓",
    active: false,
    color: "#ffe3e3",
    key: "send-survey",
  },
  {
    id: 3,
    name: "Event RSVP",
    icon: "🎉 ",
    active: false,
    color: "#f4fce3",
    key: "event-rsvp",
  },
  {
    id: 4,
    name: "Bookmark messages",
    icon: "🗃️",
    active: false,
    color: "#fff4e6",
    key: "bookmark-messages",
  },
  {
    id: 5,
    name: "Collect email",
    icon: "📧",
    active: false,
    color: "#edf2ff",
    key: "collect-email",
  },
  {
    id: 6,
    name: "Onboard user",
    icon: "🤵🏻‍♂️",
    active: false,
    color: "#e6fcf5",
    key: "public-onboarding",
  },
  {
    id: 7,
    name: "Collect feedback",
    icon: "📝",
    active: false,
    color: "#fff9db",
    key: "collect-feedback",
  },
  {
    id: 8,
    name: "Email verification",
    icon: "✅",
    active: false,
    color: "#e6fcf5",
    key: "verify-email",
  },
];

const getTriggerTemplate = (name: string, workspace: string) => {
  switch (name) {
    case "public-onboarding":
      return publicOnboardingTemplate(workspace);
    case "private-onboarding":
      return privateOnboardingTemplate(workspace);
    case "send-survey":
      return sendSurveyTemplate(workspace);
    case "support-ticket":
      return suportTicketTemplate(workspace);
    case "verify-email":
      return emailVerification(workspace);
    case "event-rsvp":
      return eventRSVP(workspace);
    case "collect-feedback":
      return collectFeedback(workspace);
    case "collect-email":
      return collectEmail(workspace);
    case "bookmark-messages":
      return bookmarkMessages(workspace);

    default:
      return publicOnboardingTemplate(workspace);
  }
};

const BotCard = ({
  name,
  icon,
  color,
  active,
  id,
  totalSelections,
  totalPossibleSelections,
  onClick,
}: {
  name: string;
  icon: string;
  color: string;
  active: boolean;
  id: number;
  totalSelections: number;
  totalPossibleSelections: number;
  onClick: (id: number) => void;
}) => (
  <div
    className={clsx(
      "rounded-xl flex cursor-pointer bg-white border-2 border-white hover:border-dots-vibrant-orange p-3 gap-3 relative",
      {
        "border-dots-vibrant-orange": active,
        "pointer-events-none opacity-40":
          totalSelections === totalPossibleSelections && !active,
      }
    )}
    onClick={() => {
      onClick(id);
    }}
  >
    {active && (
      <>
        <div className="bg-white w-5 h-5 absolute rounded-full -right-2 -top-2" />
        <CheckCircleFilled
          className="absolute -right-2 -top-2 text-xl"
          style={{ color: "#FC845E" }}
        />
      </>
    )}
    <div
      className="flex rounded-full items-center justify-center w-12 h-12"
      style={{ backgroundColor: color }}
    >
      <p className="m-0 text-3xl">{icon}</p>
    </div>
    <p className="flex-1 text-lg m-0">{name}</p>
  </div>
);

/**
 * After the user has authorized to add Dots bot AND we've setup test channels etc. The user see a message "You are live" with buttons to navigate to the test channel and edit the trigger.
 */
const DiscordAllSet = () => {
  const queryClient = useQueryClient();
  const adminChannelUrlResult = useDotsAdminsChannelUrl();
  const workspace = getWorkspace(getSupaClient());
  const navigate = useNavigate();
  const totalPossibleSelections = 3;

  const [bots, setBots] = useState<BotOption[]>(botOptions);
  const [step, setStep] = useState<number | "error">(1);
  const [editTriggerUrl, setEditTriggerUrl] = useState<string>("");

  const totalSelections = bots.filter((bot) => bot.active).length;

  useEffect(() => {
    window.analytics.track("Sign up - discord onboarded");
  }, []);

  const createAndTryTrigger = async (trigger: TriggerCreate) => {
    try {
      const guildId = localStorage.getItem("guild-id") as string;
      const createdTrigger = await persistTrigger(trigger);
      const session = await supabase.auth.session();

      window.analytics.track("Initial triggers created by user");

      // check if new trigger was retrieved.
      // index 0 = TriggerForEditor[] = new trigger inside an array.
      // index 1 = {trigger:TriggerForEditor[] ,validationErrors: string[]} = same as above on trigger key, adding validation errors as an aditional key on the same object
      if (createdTrigger && createdTrigger[0]) {
        // we extract the trigger object from the response
        const persistedTriggerRes = createdTrigger[0][0];
        if (persistedTriggerRes) {
          const triggerToTry = {
            ...persistedTriggerRes,
            liveTriggerConfigs: { type: persistedTriggerRes.type },
          };
          queryClient.invalidateQueries([
            "discord-triggers",
            { guildId, workspace },
          ]);
          await fetch(
            `${process.env["REACT_APP_API_URL"]}/triggers/${triggerToTry.id}/try`,
            {
              method: "POST",
              headers: { Authorization: `Bearer ${session?.access_token}` },
              body: JSON.stringify(triggerToTry),
            }
          );
        }
        return `/automation/${persistedTriggerRes.liveId}/edit`;
      }
      return null;
    } catch (e) {
      console.error("Error creating trigger", e);
      window.analytics.track("Error creating initial trigger", {
        errorInfo: JSON.stringify(e),
      });
      return null;
    }
  };

  const setUpTriggers = async () => {
    const urls = [];
    try {
      setStep(2);
      const user = supabase.auth.user();
      if (user?.email) {
        setOnboardingStatus(user.email, "onboarded");
        window.analytics.track("Sign up - onboarded discord");
      }
      const selectedBots = bots.filter((b) => b.active);
      for (const botObj of selectedBots) {
        const { trigger } = getTriggerTemplate(botObj.key, workspace);
        const resUrl = await createAndTryTrigger(trigger);
        if (resUrl) {
          urls.push(resUrl);
        }
      }
    } catch (error) {
      console.error("Error setting up bots", error);
    } finally {
      if (urls[0]) {
        setEditTriggerUrl(urls[0]);
        setStep(3);
      } else {
        setStep("error");
      }
    }
  };

  const onBotSelect = (botOptionID: number) => {
    const newStatus = bots.map((item) => {
      if (item.id === botOptionID) {
        return { ...item, active: !item.active };
      } else {
        return item;
      }
    });
    setBots(newStatus);
  };

  const PageLayout = ({ children }: { children: any }) => (
    <Content style={{ minHeight: "100vh" }} className="discord-all-set-bg">
      <Row justify="center" align="middle" style={{ minHeight: "100vh" }}>
        <Col span={18}>
          <div
            className="rounded-lg p-14 discord-all-set-card flex justify-center items-center"
            style={{ minHeight: "90vh" }}
          >
            {adminChannelUrlResult.isFetched ? (
              <>{children}</>
            ) : (
              <Alert
                showIcon
                message="Missing guild id, channel id or trigger id"
              ></Alert>
            )}
          </div>
        </Col>
      </Row>
    </Content>
  );

  switch (step) {
    case 1:
      return (
        <PageLayout>
          <div className="w-full">
            <p className="text-lg mb-1">
              What bots would you want to set up?{" "}
              <span className="text-dots-vibrant-orange font-normal text-sm ml-2">
                {totalSelections} / {totalPossibleSelections}
              </span>
            </p>
            <p className="text-gray-500">
              Please pick up {totalPossibleSelections}. You can add more or
              remove them after!
            </p>
            <div className="discord-all-set-grid mt-10">
              {bots.map((item) => (
                <BotCard
                  {...item}
                  key={item.id}
                  onClick={onBotSelect}
                  totalSelections={totalSelections}
                  totalPossibleSelections={totalPossibleSelections}
                />
              ))}
            </div>
            <div className="flex justify-end mt-10">
              <Button
                type="primary"
                size="large"
                style={{
                  width: "180px",
                }}
                disabled={totalSelections < totalPossibleSelections}
                onClick={() => setUpTriggers()}
              >
                Next step
              </Button>
            </div>
          </div>
        </PageLayout>
      );
    case 2:
      return (
        <PageLayout>
          <div className="flex items-center justify-center h-60 flex-col gap-2">
            <p className="font-bold">Setting up bots...</p>
            <Spin />
          </div>
        </PageLayout>
      );
    case 3:
      return (
        <PageLayout>
          <div className="w-full">
            <p>
              Amazing! Check the buttons in{" "}
              <a
                href={adminChannelUrlResult.data}
                target="_blank"
                rel="noreferrer"
              >
                <b>#dots-admins</b>
              </a>{" "}
              to try out.
            </p>
            <p className="mt-7 font-bold text-lg">What's next?</p>
            <p>
              Configure the messaging of these bots under Automation and then
              click Publish!
            </p>
            <p>Here's a video on how to edit a bot:</p>
            <div className="w-2/3 mx-auto" style={{ maxWidth: "500px" }}>
              <div
                style={{
                  position: "relative",
                  paddingBottom: "64.5933014354067%",
                  height: 0,
                }}
              >
                <iframe
                  src="https://www.loom.com/embed/017166d92f034559b761fe7a1057dbae"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                ></iframe>
              </div>
            </div>

            <div className="flex items-center justify-center mt-10">
              <Button
                type="primary"
                href={editTriggerUrl ? editTriggerUrl : "/home"}
                size="large"
              >
                Edit my first bot!
              </Button>
            </div>
          </div>
        </PageLayout>
      );
    default:
      return (
        <PageLayout>
          <div className="flex items-center justify-center h-60 flex-col gap-2">
            <Alert
              showIcon
              message={
                <p className="m-0">
                  Something went wrong, please{" "}
                  <a
                    href="https://discord.gg/nWdFSgPV"
                    target="_blank"
                    rel="noreferrer"
                  >
                    contact us.
                  </a>
                </p>
              }
            />
          </div>
        </PageLayout>
      );
  }
};

export default DiscordAllSet;
